import React from 'react';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

export type StudentProfileInfo = {
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
  defaultLanguage?: string | null;
  team?: string | null;
  position?: string | null;
  disability?: boolean | null;
};

type StudentProfileCardProps = {
  student: StudentProfileInfo;
  groupName?: string | null;
  enrollmentDate?: string | null;
  courseDefaultLanguage?: string | null;
};

const isScormNoEmail = (email?: string): boolean =>
  Boolean(email && email.toLowerCase().endsWith('@no-email.scorm.local'));

const formatDisability = (value: boolean | null | undefined): string => {
  if (value === true) return 'Oui';
  if (value === false) return 'Non';
  return '—';
};

const InfoItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1">{value}</Typography>
  </Box>
);

const StudentProfileCard: React.FC<StudentProfileCardProps> = ({
  student,
  groupName,
  enrollmentDate,
  courseDefaultLanguage,
}) => (
  <Card sx={{ mb: 3 }}>
    <CardContent>
      <Box display="flex" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <Avatar sx={{ bgcolor: 'primary.main' }}>
          {student.firstName[0]}
          {student.lastName[0]}
        </Avatar>
        <Box flex={1}>
          <Typography variant="h6">
            {student.firstName} {student.lastName}
          </Typography>
          {!isScormNoEmail(student.email) ? (
            <Typography variant="body2" color="text.secondary">
              {student.email}
            </Typography>
          ) : (
            <Chip label="SCORM sans email" size="small" color="warning" variant="outlined" sx={{ mt: 0.5 }} />
          )}
        </Box>
        <Chip
          label={student.isActive ? 'Actif' : 'Inactif'}
          color={student.isActive ? 'success' : 'default'}
          size="small"
        />
      </Box>
      <Divider sx={{ my: 2 }} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} flexWrap="wrap" useFlexGap>
        <InfoItem label="Groupe" value={groupName || 'Sans groupe'} />
        <InfoItem
          label="Inscription"
          value={
            enrollmentDate
              ? new Date(enrollmentDate).toLocaleDateString('fr-FR')
              : '—'
          }
        />
        <InfoItem
          label="Langue"
          value={student.defaultLanguage || courseDefaultLanguage || '—'}
        />
        <InfoItem label="Équipe" value={student.team || '—'} />
        <InfoItem label="Poste" value={student.position || '—'} />
        <InfoItem label="Handicap" value={formatDisability(student.disability)} />
      </Stack>
    </CardContent>
  </Card>
);

export default StudentProfileCard;
